import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../context/api';
import { FaPlus, FaTimes } from 'react-icons/fa';

const CreateInvoice = () => {
    const [invoice, setInvoice] = useState({
        classType: '',
        form: '',
        term: '',
        year: '',
        rtgsPercentage: '',
        items: [{ name: '', amount: '' }],
    });

    const [classTypes, setClassTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Fetch class types from API
    const fetchClassTypes = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/class-types`);
            setClassTypes(response.data);
        } catch (error) {
            console.error('Error fetching class types:', error);
            toast.error('Failed to load class types');
        }
    };

    useEffect(() => {
        fetchClassTypes();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInvoice({ ...invoice, [name]: value });
    };

    const handleFormChange = (e) => {
        const { value } = e.target;
        setInvoice({ ...invoice, form: value, classType: '' });
    };

    const handleItemChange = (index, e) => {
        const { name, value } = e.target;
        const items = [...invoice.items];
        items[index][name] = name === 'amount' ? parseFloat(value) : value;
        setInvoice({ ...invoice, items });
    };

    const addItem = () => {
        setInvoice({ ...invoice, items: [...invoice.items, { name: '', amount: '' }] });
    };

    const removeItem = (index) => {
        const items = [...invoice.items];
        items.splice(index, 1);
        setInvoice({ ...invoice, items });
    };

    const validateForm = () => {
        const { classType, form, term, year, rtgsPercentage, items } = invoice;

        if (!classType || !form || !term || !year || !rtgsPercentage || items.length === 0) {
            setErrorMessage('Please fill out all fields.');
            return false;
        }

        if (isNaN(year) || parseInt(year) <= 0) {
            setErrorMessage('Year must be a positive number.');
            return false;
        }

        if (isNaN(rtgsPercentage) || rtgsPercentage < 0 || rtgsPercentage > 100) {
            setErrorMessage('RTGS Percentage must be a number between 0 and 100.');
            return false;
        }

        for (let item of items) {
            if (!item.name || isNaN(item.amount) || item.amount <= 0) {
                setErrorMessage('Each item must have a valid name and a positive amount.');
                return false;
            }
        }

        setErrorMessage('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        const formattedItems = invoice.items.map(item => ({
            ...item,
            amount: parseFloat(item.amount)
        }));

        try {
            const response = await axios.post(`${BASE_URL}/invoices`, { ...invoice, items: formattedItems });
            if (response.status === 201) {
                setShowModal(true);
                setInvoice({
                    classType: '',
                    form: '',
                    term: '',
                    year: '',
                    rtgsPercentage: '',
                    items: [{ name: '', amount: '' }],
                });
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                setErrorMessage(error.response.data.message);
            } else {
                toast.error('Failed to create invoice.');
            }
        } finally {
            setLoading(false);
        }
    };

    const getFilteredClassTypes = () => {
        return classTypes;
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-5xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Create Invoice</h1>
                        <div className="h-1 w-full bg-gray-200 mt-2"></div>
                    </div>

                    {errorMessage && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-700">{errorMessage}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
                                <select
                                    name="form"
                                    value={invoice.form}
                                    onChange={handleFormChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                >
                                    <option value="">Select Form</option>
                                    {[1, 2, 3, 4, 5, 6].map(form => (
                                        <option key={form} value={form}>Form {form}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
                                <select
                                    name="classType"
                                    value={invoice.classType}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                >
                                    <option value="">Select Class Type</option>
                                    {getFilteredClassTypes().map((type) => (
                                        <option key={type.ClassTypeId} value={type.ClassTypeName}>
                                            {type.ClassTypeName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                                <select
                                    name="term"
                                    value={invoice.term}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                >
                                    <option value="">Select Term</option>
                                    <option value="1">Term 1</option>
                                    <option value="2">Term 2</option>
                                    <option value="3">Term 3</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                <input
                                    type="number"
                                    name="year"
                                    value={invoice.year}
                                    onChange={handleInputChange}
                                    placeholder="Enter year"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">RTGS Percentage</label>
                                <input
                                    type="number"
                                    name="rtgsPercentage"
                                    value={invoice.rtgsPercentage}
                                    onChange={handleInputChange}
                                    placeholder="Enter percentage"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-gray-700">Invoice Items</label>
                            {invoice.items.map((item, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Item Name"
                                        value={item.name}
                                        onChange={(e) => handleItemChange(index, e)}
                                        className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                    <input
                                        type="number"
                                        name="amount"
                                        placeholder="Amount"
                                        value={item.amount}
                                        onChange={(e) => handleItemChange(index, e)}
                                        className="w-1/3 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeItem(index)}
                                        className="p-3 text-red-500 hover:text-red-700 focus:outline-none"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addItem}
                                className="flex items-center text-pink-600 hover:text-pink-700 focus:outline-none"
                            >
                                <FaPlus className="mr-2" /> Add Item
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-pink-600 text-white py-3 px-4 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Creating Invoice...' : 'Create Invoice'}
                        </button>
                    </form>

                    {showModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                                <h3 className="text-lg font-bold text-gray-800 mb-4">Invoice Created Successfully!</h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="w-full bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateInvoice;
